import React, { useState } from 'react';
import './YamlPreview.css';

// The YAML goes in as HTML (dangerouslySetInnerHTML below) and carries text other
// people wrote — a plugin's name from the catalog, for one — so it is escaped
// BEFORE the highlighter wraps it in spans. Quotes are left alone: everything here
// is element content, and the string rule matches on them.
const escapeHtml = (text) => text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

// Syntax highlighting for YAML (light-theme colours)
export const highlightYaml = (yamlStr) => {
    if (!yamlStr) return '';
    return yamlStr.split('\n').map(line => {
        let h = escapeHtml(line);
        h = h.replace(/^(\s*)([a-zA-Z_][a-zA-Z0-9_]*)(:)/gm,
            '$1<span class="yaml-key">$2</span><span class="yaml-colon">$3</span>');
        h = h.replace(/:\s*(['"](.*?)['"])/g,
            ': <span class="yaml-string">$1</span>');
        h = h.replace(/:\s*(true|false)\s*$/gi,
            ': <span class="yaml-boolean">$1</span>');
        h = h.replace(/:\s*(\d+\.?\d*)\s*$/g,
            ': <span class="yaml-number">$1</span>');
        h = h.replace(/^(\s*)(-)(\s*)/g,
            '$1<span class="yaml-dash">$2</span>$3');
        h = h.replace(/(#.*)$/g,
            '<span class="yaml-comment">$1</span>');
        h = h.replace(/\b(all|specific|none)\b/g,
            '<span class="yaml-keyword">$1</span>');
        return h;
    }).join('\n');
};

/**
 * YamlPreview - IDE-style read-only YAML viewer.
 *
 * Props:
 *   yaml           - YAML string to display
 *   title          - Shown only when collapsible=true (default: 'YAML')
 *   collapsible    - Whether the panel can be collapsed (default: true)
 *   defaultExpanded - Whether to start expanded (default: true)
 */
const YamlPreview = ({
    yaml = '',
    title = 'YAML',
    collapsible = true,
    defaultExpanded = true,
}) => {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);
    const [copySuccess, setCopySuccess] = useState(false);

    const handleCopy = async (e) => {
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(yaml);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (_) {}
    };

    const toggleExpand = () => {
        if (collapsible) setIsExpanded(v => !v);
    };


    const lines = yaml ? yaml.split('\n') : [];

    return (
        <div className={`yaml-preview${isExpanded ? ' expanded' : ' collapsed'}`}>
            <div
                className="yaml-header"
                onClick={collapsible ? toggleExpand : undefined}
                style={collapsible ? { cursor: 'pointer' } : {}}
            >
                <div className="yaml-title">
                    {collapsible && (
                        <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                    )}
                    <span className="yaml-lang-label">YAML</span>
                    <span className="line-count">{lines.length} lines</span>
                </div>
                <div className="yaml-actions">
                    {isExpanded && (
                        <button
                            type="button"
                            className={`btn copy-btn${copySuccess ? ' success' : ''}`}
                            onClick={handleCopy}
                            title="Copy to clipboard"
                        >
                            {copySuccess ? 'Copied!' : 'Copy'}
                        </button>
                    )}
                </div>
            </div>

            {isExpanded && (
                <div className="yaml-content">
                    <div className="line-numbers" aria-hidden="true">
                        {lines.map((_, i) => (
                            <span key={i} className="line-number">{i + 1}</span>
                        ))}
                    </div>
                    <pre
                        className="yaml-code"
                        dangerouslySetInnerHTML={{ __html: highlightYaml(yaml) }}
                    />
                </div>
            )}
        </div>
    );
};

export default YamlPreview;
